import { isNative } from "./nativeHttp";
import { showSuccessToast } from "./toast";

// Native file handling. A WebView ignores anchor downloads and blob tabs, so:
//  - nativeSaveBlob: the real "download" - the file is WRITTEN to the device
//    (Android: the public Documents folder, visible in the Files app;
//    iOS: the app's Documents, exposed in the Files app through the
//    UIFileSharingEnabled/LSSupportsOpeningDocumentsInPlace plist keys).
//  - shareBlob: the system share sheet (AirDrop, Drive, mail, print...).
// Both return true when handled natively.
const blobToBase64 = (blob) => new Promise((resolve, reject) => {
  const r = new FileReader();
  r.onload = () => resolve(String(r.result).split(",")[1] || "");
  r.onerror = reject;
  r.readAsDataURL(blob);
});

const safeName = (fileName) =>
  String(fileName || "document.pdf").replace(/[\\/:*?"<>|]+/g, "-").slice(0, 120) || "document.pdf";

export async function nativeSaveBlob(blob, fileName) {
  if (!isNative || !blob) return false;
  try {
    const { Filesystem, Directory } = await import("@capacitor/filesystem");
    try {
      const st = await Filesystem.checkPermissions();
      if (st.publicStorage && st.publicStorage !== "granted") await Filesystem.requestPermissions();
    } catch { /* platforms without the permission concept */ }
    const safe = safeName(fileName);
    const data = await blobToBase64(blob);
    await Filesystem.writeFile({ path: safe, data, directory: Directory.Documents, recursive: true });
    showSuccessToast(`Saved to your Documents folder: ${safe}`);
    return true;
  } catch (e) {
    console.error("Native save failed:", e);
    // last resort: at least hand the file to the share sheet
    return shareBlob(blob, fileName);
  }
}

export async function shareBlob(blob, fileName) {
  if (!isNative || !blob) return false;
  try {
    const { Filesystem, Directory } = await import("@capacitor/filesystem");
    const { Share } = await import("@capacitor/share");
    const safe = safeName(fileName);
    const data = await blobToBase64(blob);
    const res = await Filesystem.writeFile({ path: safe, data, directory: Directory.Cache });
    await Share.share({ title: safe, url: res.uri });
    return true;
  } catch (e) {
    // closing the share sheet rejects with a cancellation: that IS handled
    if (/cancel/i.test(String((e && e.message) || ""))) return true;
    console.error("Native share failed:", e);
    return false;
  }
}
